import { motion } from "framer-motion";
import { Badge } from "@/components/ui/primitives";
import { cn, useTranslation } from "@/packages/utils";
import type { UIChecklistItem } from "@/packages/types";

interface ChecklistListItemProps {
  item: UIChecklistItem;
  isSelected: boolean;
  onClick: () => void;
  index?: number;
}

const statusColors = {
  APPROVED: { bg: "rgba(34,197,94,0.15)", text: "#16a34a" },
  UPLOADED: { bg: "rgba(59,130,246,0.15)", text: "#2563eb" },
  MISSING: { bg: "rgba(217,119,6,0.12)", text: "#b45309" },
} as const;

const deletedColor = { bg: "rgba(239,68,68,0.08)", text: "#9ca3af" };

export function ChecklistListItem({
  item,
  isSelected,
  onClick,
  index = 0,
}: ChecklistListItemProps) {
  const { t } = useTranslation("audits");
  const isDeleted = !!item.deletedAt;
  const color = isDeleted ? deletedColor : statusColors[item.status];
  const statusLabel = isDeleted
    ? t("checklist.status.deleted")
    : item.status === "APPROVED"
      ? t("checklist.status.approved")
      : item.status === "UPLOADED"
        ? t("checklist.status.uploaded")
        : t("checklist.status.missing");

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.15, delay: Math.min(index * 0.03, 0.6) }}
    >
      <button
        type="button"
        role="option"
        aria-selected={isSelected}
        onClick={onClick}
        className={cn(
          "w-full text-left px-3 py-2.5 border-b border-border transition-all duration-150",
          "hover:bg-muted/40 hover:translate-x-0.5",
          isSelected &&
            "bg-accent/10 border-l-2 border-l-primary shadow-[inset_0_0_0_0.5px_var(--color-accent-soft)]",
          !isSelected && "border-l-2 border-l-transparent",
          isDeleted && "opacity-50",
        )}
      >
        <div className="flex items-center gap-2">
          <Badge color={color} className="shrink-0 text-[10px]">
            {statusLabel}
          </Badge>
          <span
            className={cn(
              "text-sm font-medium flex-1 min-w-0",
              isDeleted && "line-through text-muted-foreground",
            )}
          >
            {item.name}
          </span>
          {item.required && !isDeleted && (
            <span className="text-[10px] text-destructive font-semibold shrink-0 tracking-wide">
              REQ
            </span>
          )}
        </div>
        {!isDeleted && item.clauseRefs && item.clauseRefs.length > 0 && (
          <div className="mt-1 pl-0 flex flex-wrap gap-1">
            {item.clauseRefs.map((ref) => (
              <span
                key={ref}
                className="inline-block rounded bg-accent-soft px-1.5 py-0.5 text-[10px] text-secondary font-medium"
              >
                {ref}
              </span>
            ))}
          </div>
        )}
      </button>
    </motion.div>
  );
}
